import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CardContent,
    Alert,
    CircularProgress,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@mui/material';
import { CloseRounded as CloseIcon } from '@mui/icons-material';
import {
    Button as RButton,
    Card as RCard,
    CardHeader as RCardHeader,
    Collapse as RCollapse,
} from 'reactstrap';
import FeatherIcon from "feather-icons-react";
import { getLeasesByProperty } from './api';
import { PreviewExistingLease } from './ExistingLeasePreview/PreviewExistingLease';
import './leases.scss';
import { LeasesGrid } from './LeaseCard';

const Leases = ({ propertyId, buildingName, unitNo }) => {
    const [leases, setLeases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedLeaseId, setSelectedLeaseId] = useState(null);

    useEffect(() => {
        const fetchLeases = async () => {
            setLoading(true);
            setError(null);
            try {
                if (!propertyId || !buildingName || !unitNo) {
                    console.warn('Missing required parameters for lease fetch', { propertyId, buildingName, unitNo });
                    setLeases([]);
                    setLoading(false);
                    return;
                }

                const response = await getLeasesByProperty(propertyId, buildingName, unitNo, 'all');

                if (!response || !response.success) {
                    throw new Error(response?.message || 'Failed to fetch leases');
                }

                setLeases(response.data || []);
            } catch (err) {
                if (err?.response?.status === 404) {
                    setLeases([]);
                } else {
                    setError(err?.message || 'Failed to fetch archived leases');
                    setLeases([]);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchLeases();
    }, [propertyId, buildingName, unitNo]);

    useEffect(() => {
        if (previewOpen) {
            window.history.pushState({ modalOpen: true }, '');

            const handlePopState = () => {
                if (previewOpen) {
                    setPreviewOpen(false);
                    window.history.pushState({ modalOpen: false }, '');
                }
            };

            window.addEventListener('popstate', handlePopState);

            return () => {
                window.removeEventListener('popstate', handlePopState);
            };
        }
    }, [previewOpen]);

    const handlePreviewOpen = (leaseId) => {
        setSelectedLeaseId(leaseId);
        setPreviewOpen(true);
    };

    const handlePreviewClose = () => {
        setPreviewOpen(false);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
                <Typography variant="body1" ml={2}>Loading archived leases...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="warning" sx={{ my: 2 }}>Unable to load archived leases. Try again later.</Alert>;
    }

    return (
        <Box>
            {leases.length === 0 ? (
                <Alert severity="info" sx={{ my: 2 }}>
                    There are no archived leases for this unit.
                </Alert>
            ) : (
                <LeasesGrid leases={leases} handlePreviewOpen={handlePreviewOpen} />
            )}

            <Dialog
                open={previewOpen}
                onClose={handlePreviewClose}
                maxWidth="md"
                fullWidth
                fullScreen
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Tooltip title="Close">
                        <IconButton onClick={handlePreviewClose} size="small" sx={{ color: 'red' }}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ height: '100vh' }}>
                        <PreviewExistingLease leaseId={selectedLeaseId} handleClose={handlePreviewClose} />
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

const LeasesWrapper = ({ propertyId, buildingName, unitNo }) => {
    const [isOpen, setIsOpen] = useState(false);

    const hasRequiredProps = Boolean(propertyId && buildingName && unitNo);
    const toggleCollapse = () => setIsOpen((prev) => !prev);

    if (!hasRequiredProps) {
        return (
            <RCard className="mb-4">
                <RCardHeader className="d-flex justify-content-between align-items-center">
                    <div className="p-0">
                        Archived Leases History
                        <Typography variant="caption" component="div">
                            Property, building and unit information are required to view archived leases.
                        </Typography>
                    </div>
                </RCardHeader>
            </RCard>
        );
    }

    return (
        <RCard className="mb-4">
            <RCardHeader className="py-3 text-center">
                <RButton
                    color="primary"
                    outline
                    className="fw-bold px-4 py-2 rounded-pill"
                    onClick={toggleCollapse}
                >
                    <FeatherIcon icon="clock" className="me-2" size={16} />
                    Archived Lease History
                </RButton>
            </RCardHeader>
            <RCollapse isOpen={isOpen}>
                <CardContent>
                    <Leases propertyId={propertyId} buildingName={buildingName} unitNo={unitNo} />
                </CardContent>
            </RCollapse>
        </RCard>
    );
};

export { LeasesWrapper as Leases };
